import backgroundImage from "@app/assets/images/background.png";
import type { DarkModeState } from "@app/shared/atoms/dark-mode.atom";
import { LeaguePointsCardHost } from "@app/league/components/LeaguePointsCard";
import { BrowserNotificationsPrompt } from "@app/shared/components/BrowserNotificationsPrompt";
import { Footer } from "@app/shared/components/Footer";
import { Header } from "@app/shared/components/Header";
import { MaintenanceContent } from "@app/shared/components/MaintenanceContent";
import { useNotificationsSocket } from "@app/shared/hooks/use-notifications-socket";
import { BaseLayout } from "@app/shared/layouts";
import {
  FullScreenLayoutProvider,
  HideInFullScreenLayout,
} from "@app/shared/layouts/FullScreenLayout";
import {
  darkModeThemeColors,
  resolveDarkModePreference,
} from "@app/shared/utils/dark-mode";
import maintenaceImg from "@assets/images/maintence.jpg";
import { User } from "@core/account/types";
import { isMobileAtom } from "@core/shared/atoms";
import { AppErrorBoundary } from "@core/shared/components";
import { useLogError, useRouteTracker } from "@core/shared/hooks";
import { Settings } from "@core/shared/types";
import { setPlatformTimeZone } from "@core/shared/utils/platform-time-zone";
import mainStyle from "@design-system/styles/main.css?url";
import { Button } from "@design-system/ui/button";
import { __DEV__, settingsFallbackMode } from "@framework/config";
import { loadSettings } from "@framework/config/cache/settings";
import { getLoaderEndpoint } from "@framework/config/drivers/loader";
import "@framework/config/translations/resource";
import {
  getSafeErrorDiagnostic,
  getSafeIdentityDiagnostic,
  getSafeSettingsHost,
  type HomeSsrRootDiagnostic,
  logHomeSsrDiagnostic,
} from "@framework/config/utils/home-ssr-diagnostics";
import {
  getDefaultSettings,
  platformClientIdAtom,
  type PlatformFeatures,
  projectPlatformClientId,
  projectPlatformFeatures,
  resetPlatformFeatures,
  settingsAtom,
} from "@framework/shared/atoms";
import { useGoogleAdsenseScript } from "@framework/shared/hooks/use-google-adsense-script.hook";
import {
  PlainLocalStorageDriver,
  RunTimeDriver,
  setCacheConfigurations,
} from "@mongez/cache";
import { setCurrentLocaleCode, trans } from "@mongez/localization";
import { GenericObject } from "@mongez/reinforcements";
import type { LinksFunction, LoaderFunction } from "@remix-run/node";
import type { ShouldRevalidateFunction } from "@remix-run/react";
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useRouteError,
  useRouteLoaderData,
} from "@remix-run/react";
import { useEffect } from "react";
import toastStyle from "sonner/dist/styles.css?url"; // the styles for sonner
import { teachingLanguagesAtom } from "../../app/shared/atoms/teaching-languages.atom";
import { useAuthRecovery } from "./auth/hooks/use-auth-recovery.hook";
import { userAtom } from "./config/atoms/user-atom";
import { handleRedirects } from "./config/drivers";
import { NavigationProgressBar } from "./shared/components/NavigationProgressBar";

// Helper function to get dark mode from cookies (server-side)
export const getDarkModeFromCookies = (
  darkModeCookie?: string | null,
): DarkModeState => resolveDarkModePreference(darkModeCookie);

/**
 * Challenge taps are graded through the play route's action; re-running the
 * root loader (settings + platform features) after every tap is wasted work.
 */
export const shouldRevalidate: ShouldRevalidateFunction = ({
  defaultShouldRevalidate,
  formAction,
  formMethod,
}) => {
  if (
    formMethod?.toUpperCase() === "POST" &&
    /^\/challenges\/[^/]+\/play\/?$/.test(formAction ?? "")
  ) {
    return false;
  }

  return defaultShouldRevalidate;
};

export const links: LinksFunction = () => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Cairo:wght@200..1000&family=Lemonada:wght@300..700&display=swap",
  },
  { rel: "stylesheet", href: mainStyle },
  { rel: "stylesheet", href: toastStyle },
];

export function ErrorBoundary() {
  const error = useRouteError() as any;
  const { logError } = useLogError();
  const user = userAtom.useValue();
  const rootLoaderData = useRouteLoaderData("root") as
    | { requestId?: string }
    | undefined;
  const referenceId = rootLoaderData?.requestId;

  // Log route errors to backend
  useEffect(() => {
    // Expected 4xx route responses (unknown URLs, bot probes) are not bugs.
    if (isRouteErrorResponse(error) && error.status < 500) return;

    if (error) {
      let errorToLog: Error;

      if (error instanceof Error) {
        errorToLog = error;
      } else if (isRouteErrorResponse(error)) {
        errorToLog = new Error(
          `Route Error: ${error.status} ${error.statusText}`,
        );
        errorToLog.stack = error.data?.message || error.statusText;
      } else {
        errorToLog = new Error("Unknown route error");
        errorToLog.stack = JSON.stringify(error);
      }

      logError(errorToLog, {
        type: "routeError",
        routeError: true,
        errorData: error,
      });
    }
  }, [error, logError]);

  // Handle maintenance mode (503 status)
  if (isRouteErrorResponse(error) && error.status === 503) {
    const maintenanceType = "general";
    const handleRetry = () => {
      window.location.reload();
    };

    return (
      <MaintenanceContent
        type={maintenanceType as "general" | "settings"}
        onRetry={handleRetry}
      />
    );
  }

  if (!__DEV__) {
    return (
      <div className="flex min-h-screen flex-col justify-between gap-5">
        <Header />
        <div className="container mx-auto mt-5 flex flex-col items-center justify-center gap-5">
          <img src={maintenaceImg} alt="" className="h-[300px]" />
          <h1 className="text-3xl font-bold text-red-500">
            {trans("error.somethingWentWrong")}
          </h1>
          <p className="text-lg font-medium text-gray-700 dark:text-gray-300">
            {trans("error.tryAgainMessage")}
          </p>
          {referenceId && (
            <p className="text-sm text-gray-500 dark:text-gray-400" dir="ltr">
              {trans("error.referenceId", { id: referenceId })}
            </p>
          )}
          <Button onClick={() => window.location.reload()}>
            {trans("tryAgain")}
          </Button>
        </div>
        <Footer />
      </div>
    );
  } else {
    if (isRouteErrorResponse(error)) {
      return (
        <div>
          <h1>
            {error.status} {error.statusText}
          </h1>
          <p>{error.data}</p>
        </div>
      );
    } else if (error instanceof Error) {
      return (
        <div className="container">
          <h1>Error:</h1>
          <p className="font- font- my-3 border border-dashed px-3 py-3 font-medium text-red-500">
            {error.message}
          </p>
          <p>The stack trace is:</p>
          <pre>{error.stack}</pre>
        </div>
      );
    } else {
      // console.log(error);
      return <h1>Unknown Error</h1>;
    }
  }
}

export const loader: LoaderFunction = async ({ request, params, context }) => {
  const {
    user,
    http,
    cookies,
    domain,
    queryString,
    setUser,
    redirectTo,
    isMobile,
    diagnosticContext,
  } = await getLoaderEndpoint(request, context);
  const result = handleRedirects({
    request,
    params,
    setUser,
    redirectTo,
  });

  if (result instanceof Response) {
    return result;
  }

  const pathname = new URL(request.url).pathname;
  const url = new URL(request.url);
  const theme = url.searchParams.get("theme") || undefined;

  // Get dark mode from cookies using the cookies object from getLoaderEndpoint
  const darkMode = getDarkModeFromCookies(cookies.get("dark-mode"));

  const { lang } = result;

  try {
    if (pathname === "/") {
      logHomeSsrDiagnostic(diagnosticContext, "root_loader_started", {
        resolvedDomain: domain,
        ...getSafeIdentityDiagnostic(user),
      });
    }

    const [settings, platformFeaturesResponse] = await Promise.all([
      loadSettings({ http, domain }),
      http.get("/platform-features").catch(() => null),
    ]);

    const platformFeaturesPayload =
      platformFeaturesResponse?.data?.data?.features ??
      platformFeaturesResponse?.data?.features ??
      {};
    const platformFeatures = projectPlatformFeatures(platformFeaturesPayload);
    const platformClientId = projectPlatformClientId(
      platformFeaturesResponse?.data?.data?.clientId ??
        platformFeaturesResponse?.data?.clientId,
    );
    const diagnostic: HomeSsrRootDiagnostic | undefined =
      pathname === "/"
        ? {
            requestId: diagnosticContext.requestId,
            domain,
            user: getSafeIdentityDiagnostic(user),
            settingsHost: getSafeSettingsHost(settings.general?.url),
          }
        : undefined;

    if (diagnostic) {
      logHomeSsrDiagnostic(diagnosticContext, "root_loader_succeeded", {
        resolvedDomain: domain,
        settingsHost: diagnostic.settingsHost,
        platformFeatureCount: Object.keys(platformFeatures).length,
        ...diagnostic.user,
      });
    }

    return {
      lang,
      user,
      settings,
      platformFeatures,
      platformClientId,
      pathname,
      theme,
      darkMode,
      isMobile,
      queryString: queryString.all(),
      diagnostic,
      requestId: diagnosticContext.requestId,
    };
  } catch (error: any) {
    if (pathname === "/") {
      logHomeSsrDiagnostic(
        diagnosticContext,
        "root_loader_failed",
        {
          resolvedDomain: domain,
          ...getSafeIdentityDiagnostic(user),
          ...getSafeErrorDiagnostic(error),
        },
        "error",
      );
    }

    // Handle authentication errors
    if (error.response?.status === 401) {
      // Remove all cookies and continue with guest user
      cookies.flush();

      return {
        lang,
        user: { userType: "guest" },
        settings: getDefaultSettings(),
        pathname,
        theme,
        darkMode,
        queryString: queryString.all(),
        diagnostic:
          pathname === "/"
            ? {
                requestId: diagnosticContext.requestId,
                domain,
                user: getSafeIdentityDiagnostic({ userType: "guest" }),
                settingsHost: getSafeSettingsHost(
                  getDefaultSettings().general?.url,
                ),
              }
            : undefined,
        requestId: diagnosticContext.requestId,
      };
    }

    // Determine if we should show maintenance mode based on error type and environment
    const shouldShowMaintenance =
      error.code === "ECONNREFUSED" ||
      (error.response?.status >= 500 && !settingsFallbackMode) || // Server errors (if fallback disabled)
      (error.response?.status === 0 && !settingsFallbackMode) || // Network errors (if fallback disabled)
      (error.message?.includes("fetch") && !settingsFallbackMode) || // Fetch errors (if fallback disabled)
      (error.message?.includes("network") && !settingsFallbackMode) || // Network errors (if fallback disabled)
      (error.message?.includes("timeout") && !settingsFallbackMode); // Timeout errors (if fallback disabled)

    if (shouldShowMaintenance) {
      // Create a custom error response with settings maintenance type
      const maintenanceError = new Response("Maintenance Mode", {
        status: 503,
      });

      throw maintenanceError;
    }

    // Return with default settings to prevent app crash
    return {
      lang,
      user,
      settings: getDefaultSettings(), // Use default settings as fallback
      pathname,
      theme,
      darkMode,
      isMobile,
      diagnostic:
        pathname === "/"
          ? {
              requestId: diagnosticContext.requestId,
              domain,
              user: getSafeIdentityDiagnostic(user),
              settingsHost: getSafeSettingsHost(
                getDefaultSettings().general?.url,
              ),
            }
          : undefined,
      requestId: diagnosticContext.requestId,
    };
  }
};

function getGoogleAdsense(code?: string) {
  if (!code) return null;

  return code;
}

export function Layout({ children }: { children: React.ReactNode }) {
  const data = useRouteLoaderData("root") as {
    lang: string;
    settings?: Settings;
    theme?: string;
    darkMode?: DarkModeState;
  };

  const favicon =
    data?.settings?.general?.favicon?.url || data?.settings?.general?.logo?.url;

  // Get theme from settings or default to navy
  const theme = data?.theme || data?.settings?.general?.theme || "navy";
  const themeClass = `theme-${theme}`;

  // Get dark mode class
  const getDarkModeClass = () => {
    return data?.darkMode === "dark" ? "dark" : "";
  };

  const darkModeClass = getDarkModeClass();

  const isDarkModeEnabled = true;

  // Injected after hydration; see useGoogleAdsenseScript.
  useGoogleAdsenseScript(
    getGoogleAdsense(data?.settings?.integrations?.googleAdsense),
  );

  return (
    <html
      lang={data?.lang || "en"}
      dir={data?.lang === "ar" ? "rtl" : "ltr"}
      className={`${themeClass}${darkModeClass ? ` ${darkModeClass}` : ""}`}
    >
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="color-scheme" content="light dark" />
        <meta
          name="theme-color"
          content={darkModeThemeColors[data?.darkMode ?? "light"]}
        />

        {/* Static meta tags for better SEO */}
        <meta name="robots" content="index, follow" />
        <meta name="referrer" content="strict-origin-when-cross-origin" />
        <meta name="x-dns-prefetch-control" content="on" />

        {/* Mobile optimization */}
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />

        {/* Site-specific meta tags */}
        <meta
          name="application-name"
          content={data?.settings?.general?.appName || "Mentor Me"}
        />
        <meta
          name="apple-mobile-web-app-title"
          content={data?.settings?.general?.appName || "Mentor Me"}
        />

        {/* Favicon */}
        {favicon && (
          <>
            <link rel="icon" href={favicon} type="image/x-icon" />
            <link rel="apple-touch-icon" href={favicon} />
          </>
        )}

        <Meta />
        <Links />

        {/* Critical script to prevent dark mode flash */}
        {isDarkModeEnabled && (
          <script
            dangerouslySetInnerHTML={{
              __html: `
              (function() {
                try {
                  // Use server-side dark mode value to prevent flash
                  const serverDarkMode = '${darkModeClass}';
                  const shouldBeDark = serverDarkMode === 'dark';
                  
                  // Apply dark mode class immediately based on server value
                  if (shouldBeDark) {
                    document.documentElement.classList.add('dark');
                  } else {
                    document.documentElement.classList.remove('dark');
                  }
                  
                  // Sync localStorage with server value to prevent future mismatches
                  const darkModeKey = 'dkm';
                  const currentStored = localStorage.getItem(darkModeKey);
                  const serverValue = shouldBeDark ? 'dark' : 'light';
                  
                  if (currentStored !== serverValue) {
                    localStorage.setItem(darkModeKey, serverValue);
                  }
                } catch (e) {
                  // Fallback: do nothing if localStorage is not available
                }
              })();
            `,
            }}
          />
        )}

      </head>
      <body
        className={`${data?.lang === "en" ? "font-SF-Pro" : "font-IBM"} bg-gray-50 transition-colors duration-300 dark:bg-gray-900`}
      >
        <ScrollRestoration />
        <NavigationProgressBar />

        <img
          src={backgroundImage}
          className="fixed bottom-0 left-0 right-0 top-0 -z-10 h-full w-full object-cover opacity-10 transition-opacity duration-300 dark:opacity-20"
          alt=""
        />
        {children}
        <Scripts />
      </body>
    </html>
  );
}

const localStorageCacheVersionKey = "cv"; // cache version
const localStorageCacheVersion = "1.0";

export default function App() {
  const {
    user,
    settings,
    pathname,
    lang,
    queryString,
    theme,
    darkMode,
    isMobile,
    platformFeatures,
    platformClientId,
    diagnostic,
  } = useLoaderData<{
    lang: string;
    user: User;
    settings: Settings;
    layout: any;
    pathname: string;
    queryString: GenericObject;
    theme?: string;
    darkMode?: DarkModeState;
    isMobile?: boolean;
    platformFeatures?: PlatformFeatures;
    platformClientId?: number | null;
    diagnostic?: HomeSsrRootDiagnostic;
  }>();

  settingsAtom.update(settings);
  // Format every user-visible date in the tenant/platform zone on both sides.
  setPlatformTimeZone(settings?.general?.timezone);
  resetPlatformFeatures(platformFeatures);
  platformClientIdAtom.update(platformClientId ?? null);

  userAtom.update(user);

  if (typeof window === "undefined" && diagnostic) {
    const observedUser = getSafeIdentityDiagnostic(userAtom.value);
    const observedSettingsHost = getSafeSettingsHost(
      settingsAtom.value.general?.url,
    );

    logHomeSsrDiagnostic(diagnostic, "root_atoms_seeded", {
      resolvedDomain: diagnostic.domain,
      expectedSettingsHost: diagnostic.settingsHost,
      observedSettingsHost,
      expectedUserId: diagnostic.user.userId,
      observedUserId: observedUser.userId,
      expectedUserType: diagnostic.user.userType,
      observedUserType: observedUser.userType,
      userIdentityMatches:
        diagnostic.user.userId === observedUser.userId &&
        diagnostic.user.userType === observedUser.userType,
      settingsHostMatches: diagnostic.settingsHost === observedSettingsHost,
    });
  }

  setCurrentLocaleCode(lang);

  // Initialize auth recovery for mobile cookie issues
  useAuthRecovery();
  isMobileAtom.update(isMobile || false);

  // Initialize route tracking for error logging
  useRouteTracker();

  // Keep realtime notifications connected for every authenticated student
  // route, including layouts that do not render the header notification bell.
  useNotificationsSocket();

  if (
    settings?.general?.teachingLanguages &&
    settings?.general?.teachingLanguages?.length > 1
  ) {
    teachingLanguagesAtom.update({
      currentLanguage:
        queryString?.teachingLanguage || settings.general.teachingLanguages[0],
      languages: settings.general.teachingLanguages,
    });
  } else {
    teachingLanguagesAtom.reset();
  }

  if (typeof window !== "undefined") {
    try {
      const cacheVersionInLocalStorage = localStorage.getItem(
        localStorageCacheVersionKey,
      );
      if (cacheVersionInLocalStorage !== localStorageCacheVersion) {
        localStorage.clear();
        localStorage.setItem(
          localStorageCacheVersionKey,
          localStorageCacheVersion,
        );
        return window.location.reload();
      }
      setCacheConfigurations({
        driver: new PlainLocalStorageDriver(),
        prefix: "mntrm",
      });
    } catch (error) {
      // localStorage is not available (privacy mode, iframe restrictions, etc.)
      // Fall back to an in-memory cache: without any driver every
      // `cache.get/set/remove` call threw "reading 'get' of undefined".
      console.warn("localStorage is not available:", error);
      setCacheConfigurations({
        driver: new RunTimeDriver(),
        prefix: "mntrm",
      });
    }
  }

  // useAppSecurity();

  return (
    <AppErrorBoundary>
      {/* A live quiz attempt hides the site chrome (FullScreenLayout). */}
      <FullScreenLayoutProvider>
        <BaseLayout pathname={pathname}>
          <Outlet />
        </BaseLayout>
        <HideInFullScreenLayout>
          <BrowserNotificationsPrompt />
        </HideInFullScreenLayout>
        <LeaguePointsCardHost />
      </FullScreenLayoutProvider>
    </AppErrorBoundary>
  );
}
